/**
 * Every relay a farm's controllers can address, annotated with what already
 * claims it.
 *
 * The hydraulic model only sees a valve that a schedule opens if some
 * `irrigation_nodes` row carries that relay's `(router_mac, relay_index)` —
 * that link is what `scheduleAnalysisService` looks a step's relays up by, and
 * an unmatched one comes back as "unmapped relay" with the step silently
 * solving as though that valve were shut. Making the link is therefore a
 * routine operator job, and it needs a list of what is out there.
 *
 * Three things this has to say about a slot, because the operator's next action
 * differs for each:
 *
 *   - **a program drives it and nothing is linked** — the actionable case, and
 *     the one the hydraulic check is already complaining about;
 *   - **another node holds it** — linking here would move a valve, so say which
 *     node rather than letting the attempt fail on the unique index;
 *   - **nobody uses it** — free, but also possibly not wired to anything.
 */

import {
  usableRelays,
  type EspNowController,
  type EspNowRouter,
  type RelayZone,
  type ScheduleProgram,
} from '../services/espnowIrrigation';
import { nodeState, type NodeState } from './nodeState';
import type { IrrigationNode } from '../services/irrigationNetwork';

export interface RelaySlot {
  router_mac: string;
  relay_index: number;
  controller_id: string;
  controller_name: string | null;
  router_name: string | null;
  router_state: NodeState;
  /** The operator's label for this output, or `Relay <n>` when it has none. */
  label: string;
  /**
   * Marked by an operator as wired to nothing. Still offered when a program or
   * a node already uses it — an out-of-use relay that is nonetheless scheduled
   * is exactly the mismatch worth seeing, and hiding it hides the fix too.
   */
  disabled: boolean;
  /** Names of the programs whose steps energise this relay. */
  programs: string[];
  /** The irrigation node that holds this relay, if any. */
  linked_node_id: number | null;
  linked_node_name: string | null;
  /**
   * The outline drawn around what this relay waters.
   *
   * Belongs to the relay, not to the node — which is why a valve reaches its
   * area THROUGH its relay link rather than storing a second one. Two links
   * could disagree, and nothing would reconcile them.
   *
   * A zone row can exist with `boundary_geometry: null`: named and coloured,
   * never drawn. That is not the same as having no zone at all, and the UI has
   * to be able to tell them apart.
   */
  zone: RelayZone | null;
}

/** Relay keys are compared case-insensitively; MACs reach us in both cases. */
function key(mac: string, index: number): string {
  return `${mac.toUpperCase()}:${index}`;
}

export function slotKey(slot: RelaySlot): string {
  return key(slot.router_mac, slot.relay_index);
}

function nodeLabel(n: IrrigationNode): string {
  return n.name || `${n.node_type} #${n.id}`;
}

function routerLabel(r: EspNowRouter): string {
  return r.name || r.router_mac.slice(-8);
}

export interface CatalogueInput {
  controllers: EspNowController[];
  /** Routers by controller_id, as `getRouters` returns them. */
  routersByController: Record<string, EspNowRouter[]>;
  /** Programs by controller_id, as `getPrograms` returns them. */
  programsByController: Record<string, ScheduleProgram[]>;
  /** Drawn areas by controller_id, as `getRelayZones` returns them. */
  zonesByController?: Record<string, RelayZone[]>;
  /** Every node on the farm — the claims come from these. */
  nodes: IrrigationNode[];
}

/**
 * Build the farm's relay list, ordered the way an operator walks the site:
 * controller, then node, then relay number.
 *
 * Which indices a node contributes is `usableRelays()` — never a local copy of
 * the count rule, which is how the three existing relay lists would drift apart
 * — plus any index a program or an existing link already names. Those extras
 * matter: a board that reports 4 relays but carries a schedule row on relay 5
 * has a real problem, and leaving the row out of the list leaves it unfixable.
 */
export function buildRelayCatalogue(input: CatalogueInput): RelaySlot[] {
  const { controllers, routersByController, programsByController, nodes } = input;
  const zonesByController = input.zonesByController ?? {};

  const claims = new Map<string, IrrigationNode>();
  for (const n of nodes) {
    if (n.relay_router_mac && n.relay_index !== null) {
      claims.set(key(n.relay_router_mac, n.relay_index), n);
    }
  }

  const slots: RelaySlot[] = [];

  for (const c of controllers) {
    const routers = routersByController[c.controller_id] ?? [];
    const programs = programsByController[c.controller_id] ?? [];

    // program names per relay key, in program order, without duplicates
    const programsByRelay = new Map<string, string[]>();
    for (const p of programs) {
      for (const step of p.steps ?? []) {
        for (const rel of step.relays ?? []) {
          if (!rel.router_mac) continue;
          const k = key(rel.router_mac, rel.relay_index);
          const names = programsByRelay.get(k) ?? [];
          if (!names.includes(p.name)) names.push(p.name);
          programsByRelay.set(k, names);
        }
      }
    }

    const zoneByRelay = new Map<string, RelayZone>();
    for (const z of zonesByController[c.controller_id] ?? []) {
      zoneByRelay.set(key(z.router_mac, z.relay_index), z);
    }

    for (const r of routers) {
      // No transport argument: an `EspNowRouter` carries its own, and passing
      // the controller's would override it.
      const offered = new Set(usableRelays(r));
      const off = new Set(r.disabled_relays ?? []);

      // An index nobody would otherwise offer still belongs in the list when
      // something already points at it.
      for (const k of programsByRelay.keys()) {
        if (k.startsWith(`${r.router_mac.toUpperCase()}:`)) {
          offered.add(Number(k.slice(k.lastIndexOf(':') + 1)));
        }
      }
      for (const k of claims.keys()) {
        if (k.startsWith(`${r.router_mac.toUpperCase()}:`)) {
          offered.add(Number(k.slice(k.lastIndexOf(':') + 1)));
        }
      }
      for (const k of zoneByRelay.keys()) {
        if (k.startsWith(`${r.router_mac.toUpperCase()}:`)) {
          offered.add(Number(k.slice(k.lastIndexOf(':') + 1)));
        }
      }

      for (const i of [...offered].sort((a, b) => a - b)) {
        const k = key(r.router_mac, i);
        const held = claims.get(k) ?? null;
        slots.push({
          router_mac: r.router_mac,
          relay_index: i,
          controller_id: c.controller_id,
          controller_name: c.controller_name,
          router_name: r.name,
          router_state: nodeState(r),
          label: r.relay_labels?.[String(i)] || `Relay ${i}`,
          disabled: off.has(i),
          programs: programsByRelay.get(k) ?? [],
          linked_node_id: held ? held.id : null,
          linked_node_name: held ? nodeLabel(held) : null,
          zone: zoneByRelay.get(k) ?? null,
        });
      }
    }
  }

  return slots;
}

/**
 * The slots a schedule waters that no valve node represents.
 *
 * This is the set behind every "unmapped relay" the hydraulic check reports, so
 * it is what the linking UI should put in front of the operator first — a flat
 * list of every relay on the farm buries it.
 */
export function unlinkedProgramSlots(catalogue: RelaySlot[]): RelaySlot[] {
  return catalogue.filter(s => s.programs.length > 0 && s.linked_node_id === null);
}

/** One line describing what, if anything, already uses a slot. */
export function slotClaim(slot: RelaySlot, currentNodeId?: number): string | null {
  if (slot.linked_node_id !== null) {
    return slot.linked_node_id === currentNodeId
      ? 'linked here'
      : `linked to ${slot.linked_node_name}`;
  }
  if (slot.programs.length > 0) {
    return slot.programs.length === 1
      ? `in ${slot.programs[0]} — not linked`
      : `in ${slot.programs.length} programs — not linked`;
  }
  return null;
}

/**
 * Router groupings for a picker, preserving catalogue order.
 *
 * Grouped by node rather than listed flat because that is how the hardware is
 * laid out: an operator standing at a box is choosing between its four relays,
 * not between forty.
 */
export interface RelayGroup {
  router_mac: string;
  title: string;
  controller_name: string | null;
  router_state: NodeState;
  slots: RelaySlot[];
}

export function groupByRouter(
  catalogue: RelaySlot[],
  routersByController: Record<string, EspNowRouter[]>,
): RelayGroup[] {
  const byMac = new Map<string, RelayGroup>();
  const titles = new Map<string, string>();
  for (const list of Object.values(routersByController)) {
    for (const r of list) titles.set(r.router_mac.toUpperCase(), routerLabel(r));
  }

  for (const s of catalogue) {
    const mac = s.router_mac.toUpperCase();
    if (!byMac.has(mac)) {
      byMac.set(mac, {
        router_mac: s.router_mac,
        title: titles.get(mac) ?? s.router_name ?? s.router_mac.slice(-8),
        controller_name: s.controller_name,
        router_state: s.router_state,
        slots: [],
      });
    }
    byMac.get(mac)!.slots.push(s);
  }

  return [...byMac.values()];
}
